import { createContext, useContext } from 'react';

/**
 * Coordinación de la transición **slide/push** entre los pasos del "Account setup"
 * (ADR-736, supersede el fade de ADR-715).
 *
 * El layout (`AccountSetupLayout`) mantiene montadas **ambas** pantallas durante la
 * transición y le pasa a cada una su fase vía este contexto:
 * - `enter`: la que entra (arranca corrida y se acomoda a 0).
 * - `leave`: la que se va (se corre hacia afuera empujada por la que entra).
 * - `idle`: en reposo (sin transición).
 *
 * `dir` define el sentido del empuje:
 * - `forward` (avanza): la nueva entra desde la **derecha** y empuja a la anterior
 *   hacia la izquierda.
 * - `back` (retrocede): al revés.
 *
 * El header (back + barra de progreso + "N de N") queda **fijo** en el layout y la
 * barra se anima sola (un único `SetupProgress` persistente cuyo `step` cambia). El
 * footer (CTA) hace **fade in/out**, no slide (lo maneja `SetupScaffold`).
 */
export type StepDir = 'forward' | 'back';
export type StepPhase = 'enter' | 'leave' | 'idle';

export interface StepPhaseValue {
  phase: StepPhase;
  dir: StepDir;
}

/** Duración del slide de la pantalla (ms). El layout usa este valor para saber
 *  cuándo desmontar la pantalla saliente. Coincide con el `transition` del scaffold
 *  y con la barra de progreso (`SetupProgress`), para que todo viaje al mismo ritmo.
 *  Duplicado (320→640) para una transición más sutil y lenta. */
export const SETUP_SLIDE_MS = 640;

/** Duración del fade in/out del footer/CTA (ms). Un toque más corto que el slide
 *  para que el botón aparezca/desaparezca con nitidez. Duplicado junto con el slide. */
export const SETUP_FADE_MS = 440;

/** Curva del slide (ease-out expresiva, arranca rápido y desacelera). */
export const SETUP_SLIDE_EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';

export const StepPhaseContext = createContext<StepPhaseValue>({ phase: 'idle', dir: 'forward' });

/** Fase/sentido de la pantalla actual dentro de la transición del cuestionario. */
export function useStepPhase(): StepPhaseValue {
  return useContext(StepPhaseContext);
}
